import {
  Component,
  inject,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzCheckboxComponent, NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
} from 'ng-zorro-antd/form';
import { NzIconModule, NzIconService } from 'ng-zorro-antd/icon';
import { NzInputGroupComponent } from 'ng-zorro-antd/input';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import {
  UserOutline,
  EyeInvisibleOutline,
} from '@ant-design/icons-angular/icons';
import { AuthService } from '../../../core/services/auth.service';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { DrawerService } from '../../../core/services/drawer.service';
import { ReturnUrlService } from '../../../core/services/return-url.service';
import { ErrorResponse } from '../../../core/models/error.models';
import { SsoOrganization } from '../../../core/models/auth.models';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NzFormControlComponent,
    NzInputGroupComponent,
    NzFormItemComponent,
    NzButtonComponent,
    NzCheckboxComponent,
    NzFormLabelComponent,
    NzIconModule,
    NzCheckboxModule,
    NzRadioModule,
    SvgIconComponent,
    CommonModule,
    RouterModule,
    FormsModule,
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit, OnDestroy {
  passwordVisible = false;
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly returnUrlService = inject(ReturnUrlService);
  private readonly destroy$ = new Subject<void>();
  private readonly nzIconService = inject(NzIconService);

  isLoading = false;
  errorMessage = '';
  rememberMe = false;
  fullReturnUrl: string | null = null;
  showPasswordField = false;
  ssoOrganizations: SsoOrganization[] = [];
  selectedIdpName: string | null = null;
  showSsoSelection = false;
  private readonly STORAGE_KEY = 'returnUrl';

  constructor(private readonly drawerService: DrawerService) {
    this.nzIconService.addIcon(UserOutline, EyeInvisibleOutline);
    localStorage.setItem('isLMSLoggedIn', 'false');
    localStorage.removeItem('verify-user');
  }

  readonly loginForm: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required]],
  });

  ngOnInit(): void {
    this.drawerService.closeAllDrawers();
    this.fullReturnUrl = this.route.snapshot.queryParams['returnUrl'];
    if (this.fullReturnUrl) {
      localStorage.setItem(
        this.STORAGE_KEY,
        decodeURIComponent(this.fullReturnUrl)
      );
    }
    const errorMessageParam = this.route.snapshot.queryParams['errorMessage'];
    if (errorMessageParam) {
      this.errorMessage = errorMessageParam;
    }

    this.authService.authState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        if (state.isAuthenticated) {
          const returnUrl = this.returnUrlService.getReturnUrl();
          this.router.navigate([returnUrl]);
        } else {
          const data = localStorage.getItem('login_data');
          if (data) {
            const login_data = this.authService.decryptData(data);
            if (login_data) {
              this.loginForm.patchValue(login_data);
              this.rememberMe = true;
            }
          }
        }
      });
  }

  handleFormSubmit(): void {
    if (this.showSsoSelection) {
      this.initiateSamlLogin();
    } else if (this.showPasswordField) {
      this.onSubmit();
    } else {
      this.checkSsoLogin();
    }
  }

  checkSsoLogin(): void {
    const emailControl = this.loginForm.get('email');

    if (emailControl?.valid && emailControl.value) {
      this.isLoading = true;
      this.errorMessage = '';

      this.authService
        .signInSso(emailControl.value)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            this.fullReturnUrl = this.route.snapshot.queryParams['returnUrl'];
            if (this.fullReturnUrl) {
              localStorage.setItem(
                this.STORAGE_KEY,
                decodeURIComponent(this.fullReturnUrl)
              );
            }
            if (!response.result || response.result.length === 0) {
              this.isLoading = false;
              this.showPasswordField = true;
            } else {
              this.ssoOrganizations = response.result;
              localStorage.setItem('sso-email', emailControl.value);
              this.showSsoSelection = true;

              if (this.ssoOrganizations.length === 1) {
                this.selectedIdpName = this.ssoOrganizations[0].idpName;

                this.initiateSamlLogin();
              } else {
                this.isLoading = false;
              }
            }
          },
          error: (error: ErrorResponse) => {
            this.isLoading = false;
            this.errorMessage = error.message;
          },
        });
    }
  }

  initiateSamlLogin(): void {
    if (!this.selectedIdpName) {
      this.errorMessage = 'Please select an identity provider';
      return;
    }

    localStorage.setItem('idpName', this.selectedIdpName);
    this.returnUrlService.setReturnUrl(this.fullReturnUrl);

    const emailControl = this.loginForm.get('email');
    const email = emailControl?.value || '';

    globalThis.location.href = `${this.authService.API_URL}/auth/saml/sso-initiate?idpName=${encodeURIComponent(this.selectedIdpName)}&client=web&email=${encodeURIComponent(email)}`;
  }

  selectIdentityProvider(idpName: string): void {
    this.selectedIdpName = idpName;

    if (this.ssoOrganizations.length > 1) {
      this.initiateSamlLogin();
    }
  }

  onSubmit(): void {
    if (this.loginForm.valid) {
      if (!this.rememberMe) {
        localStorage.removeItem('login_data');
      }
      this.isLoading = true;
      this.authService
        .sentOtp(this.loginForm.value)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            if (this.rememberMe) {
              const encrypt = this.authService.encryptData(
                this.loginForm.value
              );
              localStorage.setItem('login_data', encrypt);
            }
            localStorage.setItem('isLMSLoggedIn', 'false');

            this.router.navigate(['/verify-user'], {
              queryParams: {
                returnUrl: this.returnUrlService.getReturnUrl(),
                stageryLink: this.fullReturnUrl?.includes('strategy')
                  ? this.fullReturnUrl
                  : null,
              },
            });
          },
          error: (error: ErrorResponse) => {
            this.isLoading = false;

            if (error.errors && error.errors[0]?.field === 'OTP_ALREADY_SENT') {
              const userData = this.authService.encryptData(
                this.loginForm.value
              );
              localStorage.setItem('verify-user', userData);
              this.router.navigate(['/verify-user'], {
                queryParams: {
                  returnUrl: this.returnUrlService.getReturnUrl(),
                  otpGenerated: true,
                },
              });
            }

            this.errorMessage = error.message;
          },
          complete: () => {
            this.isLoading = false;
          },
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
